import { Component, effect, inject, Input } from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import { WidgetStore } from '../../../stores/grid/widget.state';
import { getState } from '@ngrx/signals';
import {
  makeUniqueNumberArray,
  makeUniqueStringArray,
} from '../../../utility/array.utility';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { PaginatorModule } from 'primeng/paginator';
import { ReactiveFormsModule } from '@angular/forms';
import { ChangeContext, NgxSliderModule } from '@angular-slider/ngx-slider';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { TooltipModule } from 'primeng/tooltip';
import { GridStore } from '../../../stores/grid/grid.state';

@Component({
  selector: 'app-atlas-year-controls',
  standalone: true,
  imports: [
    PaginatorModule,
    ReactiveFormsModule,
    NgxSliderModule,
    TooltipModule,
  ],
  templateUrl: './atlas-year-controls.component.html',
  styleUrl: './atlas-year-controls.component.scss',
})
export class AtlasYearControlsComponent {
  readonly widgetStore = inject(WidgetStore);
  readonly gridStore = inject(GridStore);
  readonly layerStore = inject(DataLayerStore);
  readonly globalStore = inject(GlobalsStore);

  @Input() disableControls = false;
  currentYearsInView: number[] = [];
  availableYears: number[] = [];
  sliderOptions: any = {};
  allowOneYearFocusDropdown = false;

  constructor() {
    effect(() => {
      const widgetState = getState(this.widgetStore);
      this.setupSlider();
    });
  }

  /**
   * Configures and initializes the slider component by setting the current years,
   * available years, and slider options.
   *
   * @return {void} Does not return any value.
   */
  setupSlider(): void {
    this.setCurrentYears();
    this.setAvailableYears();
    this.setSliderOptions();
  }

  /**
   * Sets the current years in view by retrieving widgets with a specific focus property,
   * extracting their associated years, ensuring they are unique, and sorting the resulting array.
   *
   * @return {void} This method does not return a value.
   */
  setCurrentYears(): void {
    const widgets = this.widgetStore.getByFocusProperty('dataLayer');
    this.currentYearsInView = makeUniqueNumberArray(
      widgets.map(widget => Number(widget.widget.year))
    ).sort();
  }

  /**
   * Updates the available years based on data layer types retrieved from widgets.
   *
   * The method retrieves widgets with a specific focus property, extracts their data layer types,
   * eliminates duplicates, and updates the `availableYears` property using those types.
   *
   * @return {void}
   */
  setAvailableYears(): void {
    const widgets = this.widgetStore.getByFocusProperty('dataLayer');
    const dataLayerTypes = makeUniqueStringArray(
      widgets
        .filter(widget => widget.widget.context === 'local')
        .map(widget => widget.widget.dataLayerType)
    );

    this.availableYears = this.layerStore.getYearsByParents(dataLayerTypes);
  }

  /**
   * Generates a caption string representing a range of years
   * based on the current years in view.
   *
   * @return {string} A single year string if only one year is in view,
   * or a range of years formatted as "startYear - endYear" if multiple years are in view.
   */
  getCaption(): string {
    if (this.currentYearsInView.length === 1) {
      return String(this.currentYearsInView.at(0));
    } else {
      return `${this.currentYearsInView.at(0)} - ${this.currentYearsInView.at(this.currentYearsInView.length - 1)}`;
    }
  }

  /**
   * Configures and sets the slider options based on the current years in view and available years.
   * Adjusts properties such as range settings and allows for a focused dropdown based on whether a range slider is needed.
   * If there are no years in the current view or available years, the slider options are set to null.
   *
   * @return {void} This method does not return any value.
   */
  setSliderOptions(): void {
    if (!this.currentYearsInView.length || !this.availableYears.length) {
      this.sliderOptions = null;
      return;
    }
    const isRangeSlider = this.currentYearsInView.length > 1;
    this.allowOneYearFocusDropdown = isRangeSlider;
    let sliderOptions: any = {};
    if (isRangeSlider) {
      sliderOptions = {
        minValue: this.currentYearsInView.at(0),
        maxValue: this.currentYearsInView.at(
          this.currentYearsInView.length - 1
        ),
      };
    } else {
      sliderOptions = {
        minValue: this.currentYearsInView.at(0),
      };
    }

    sliderOptions.options = {
      floor: this.availableYears.at(0),
      ceil: this.availableYears.at(this.availableYears.length - 1),
      draggableRangeOnly: true,
      showTicks: true,
    };

    this.sliderOptions = { ...sliderOptions };
  }

  /**
   * Retrieves the lowest year currently in view.
   *
   * @return {number|undefined} The first year in the currentYearsInView array, or undefined if the array is empty.
   */
  get lowYear(): number | undefined {
    return this.currentYearsInView.at(0);
  }

  /**
   * Retrieves the highest year currently in view from the list of years.
   *
   * @return {number} The highest year in the `currentYearsInView` array.
   */
  get highYear(): number | undefined {
    return this.currentYearsInView.at(this.currentYearsInView.length - 1);
  }

  /**
   * Updates the 'year' property for widgets based on the given event's value
   * and adjusts associated data layers or properties accordingly.
   *
   * @param {$event} $event - Represents the event that contains the new value
   *                          which determines how the widget years should be updated.
   *                          Can be of type ChangeContext or DropdownChangeEvent.
   *
   * @return {void} - This method updates the state of widgets in the
   *                  widget store but does not return any value.
   */
  setNewYears($event: ChangeContext | DropdownChangeEvent): void {
    const newWidgets = this.widgetStore
      .getByFocusProperty('dataLayer')
      .map(widget => {
        const dataLayerType = widget.widget.dataLayerType;
        const dataLayer = widget.widget.dataLayer;
        const currentYear = Number(widget.widget.year);
        let newYear;
        if (currentYear && this.lowYear) {
          if ($event.value < this.lowYear) {
            newYear = currentYear - (this.lowYear - $event.value);
          } else if ($event.value > this.lowYear) {
            newYear = currentYear + ($event.value - this.lowYear);
          } else {
            newYear = currentYear;
          }

          if (newYear === currentYear) return widget;
          const newLayer = this.layerStore.filterByParentAndYear(
            dataLayerType,
            newYear
          );

          if (newLayer && widget.widget.context !== 'external') {
            widget.widget.year = String(newYear);
            widget.widget.dataLayer = newLayer._id;
            this.widgetStore.updateDistricts(widget.id, dataLayer);
            this.widgetStore.updateChartData(widget.id);
          } else if (widget.widget.context === 'external') {
            const wegweiserData = widget.widget.wegweiserData;
            if (!wegweiserData) return widget;
            const wegweiserHasYear: boolean =
              wegweiserData.years.indexOf(Number(newYear)) > -1;
            widget.widget.year = String(
              wegweiserHasYear ? newYear : currentYear
            );
          }
          return widget;
        }
        return widget;
      });

    this.widgetStore.bulkUpdate(newWidgets);
    setTimeout((): void => {
      this.gridStore.triggerLayoutChanges();
    }, 300);
  }

  /**
   * Updates widgets to focus on the selected year by modifying their data layer and related properties.
   * Filters widgets based on 'dataLayer', updates their year, recalculates district configurations,
   * and updates chart data for affected widgets.
   *
   * @param {DropdownChangeEvent} $event - The event object containing the selected value for the year.
   * @return {void} No return value.
   */
  focusOnYear($event: DropdownChangeEvent): void {
    const newWidgets = this.widgetStore
      .getByFocusProperty('dataLayer')
      .map(widget => {
        const dataLayerType = widget.widget.dataLayerType;
        const dataLayer = widget.widget.dataLayer;
        const newLayer = this.layerStore.filterByParentAndYear(
          dataLayerType,
          $event.value
        );

        if (newLayer) {
          widget.widget.year = String($event.value);
          widget.widget.dataLayer = newLayer._id;
          this.widgetStore.updateDistricts(widget.id, dataLayer);
          this.widgetStore.updateChartData(widget.id);
        }
        return widget;
      });

    this.widgetStore.bulkUpdate(newWidgets);
    setTimeout((): void => {
      this.gridStore.triggerLayoutChanges();
    }, 300);
  }
}
